import React, { useEffect, useState, Children } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Package, Mail, LogOut, ArrowRight, CheckCircle2, Clock, Truck, Download, AlertCircle, X, MapPin, Plus, Minus, Trash2, Edit2, Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, type Order, type OrderLineItem, type Address } from '../../../utils/firebase';
import { useCart } from '../../../contexts/CartContext';
import { recordProductSales } from '../../../utils/firebase';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { shopData } from './shopData';
import { bookData } from './bookData';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function getItemImage(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const name = item.name?.trim().toLowerCase();
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => b.title.trim().toLowerCase() === name);
    return book?.coverImage;
  }
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => s.name.trim().toLowerCase() === name);
  return product?.image;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      // Open the signed URL in a new tab so the user keeps their account page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col items-start gap-2" data-id="element-1570">
      <button type="button" onClick={handleClick} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-1571">
        {loading ? <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1572" />
            Preparing…
          </> : <>
            <Download className="w-3.5 h-3.5" data-id="element-1573" />
            Download
          </>}
      </button>
      {error && <p className="text-[10px] text-burgundy leading-relaxed max-w-xs" data-id="element-1574">
          {error}
        </p>}
    </div>;
}
function FulfillmentBadge({
  order
}: {
  order: Order;
}) {
  const config = (() => {
    switch (order.fulfillmentStatus) {
      case 'digital_delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'delivered':
        return {
          icon: CheckCircle2,
          label: 'Delivered',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'shipped':
        return {
          icon: Truck,
          label: 'Shipped',
          color: 'text-tealslate',
          bg: 'bg-tealslate/10'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Processing',
          color: 'text-burgundy',
          bg: 'bg-burgundy/10'
        };
    }
  })();
  const Icon = config.icon;
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.color} text-[10px] font-bold uppercase tracking-[0.2em] rounded-md`} data-id="element-1575">
      <Icon className="w-3 h-3" data-id="element-1576" />
      {config.label}
    </span>;
}
function EditInfoModal({
  currentName,
  currentEmail,
  onClose,
  onSaveName,
  onSaveEmail
}: {
  currentName: string;
  currentEmail: string;
  onClose: () => void;
  onSaveName: (name: string) => Promise<void>;
  onSaveEmail: (email: string, password: string) => Promise<void>;
}) {
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== currentEmail.toLowerCase();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (name.trim() !== currentName) {
        await onSaveName(name);
      }
      if (emailChanged) {
        if (!password) {
          throw new Error('Current password is required to change email.');
        }
        await onSaveEmail(email, password);
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1577">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1578" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1579">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1580">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1581">Edit Information</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1582">
            <X className="w-5 h-5" data-id="element-1583" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1584">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1585">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1586">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1587" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1588">
                Information Updated
              </p>
              {emailChanged && <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1589">
                  A verification link has been sent to your new email address.
                  The change will take effect once you confirm it.
                </p>}
            </div> : <>
              <div data-id="element-1590">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1591">
                  Full Name
                </label>
                <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1592" />
              </div>

              <div data-id="element-1593">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1594">
                  Email Address
                </label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1595" />
              </div>

              {emailChanged && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="overflow-hidden" data-id="element-1596">
                  <div className="pt-2" data-id="element-1597">
                    <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1598">
                      Current Password *
                    </label>
                    <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1599" />
                    <p className="text-xs text-[#5A5A5A] mt-2 leading-relaxed" data-id="element-1600">
                      For security, your current password is required to change
                      your email address.
                    </p>
                  </div>
                </motion.div>}

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1601">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1602" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1603">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1604">
                <button type="submit" disabled={loading || !name.trim() && !emailChanged} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1605">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1606" />
                      Saving...
                    </> : 'Save Changes'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
function ChangePasswordModal({
  onClose,
  onChangePassword
}: {
  onClose: () => void;
  onChangePassword: (current: string, next: string) => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1607">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm" data-id="element-1608" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden" data-id="element-1609">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]" data-id="element-1610">
          <h2 className="text-xl font-bold text-[#111111]" data-id="element-1611">Change Password</h2>
          <button onClick={onClose} className="text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1612">
            <X className="w-5 h-5" data-id="element-1613" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-1614">
          {success ? <div className="flex flex-col items-center justify-center py-8 text-center" data-id="element-1615">
              <div className="w-12 h-12 bg-tealslate/10 rounded-full flex items-center justify-center mb-4" data-id="element-1616">
                <CheckCircle2 className="w-6 h-6 text-tealslate" data-id="element-1617" />
              </div>
              <p className="text-lg font-bold text-[#111111] mb-2" data-id="element-1618">
                Password Changed
              </p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1619">
                Your password has been successfully updated.
              </p>
            </div> : <>
              <div data-id="element-1620">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1621">
                  Current Password
                </label>
                <input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1622" />
              </div>

              <div data-id="element-1623">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1624">
                  New Password
                </label>
                <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1625" />
              </div>

              <div data-id="element-1626">
                <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1627">
                  Confirm New Password
                </label>
                <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1628" />
              </div>

              {error && <div className="flex items-start gap-2 p-3 bg-burgundy/5 rounded-md" data-id="element-1629">
                  <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1630" />
                  <p className="text-xs text-burgundy leading-relaxed" data-id="element-1631">
                    {error}
                  </p>
                </div>}

              <div className="pt-4" data-id="element-1632">
                <button type="submit" disabled={loading || !currentPassword || !newPassword || !confirmPassword} className="w-full px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1633">
                  {loading ? <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1634" />
                      Updating...
                    </> : 'Update Password'}
                </button>
              </div>
            </>}
        </form>
      </motion.div>
    </div>;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    resendVerification,
    updateDisplayName,
    updateEmail,
    changePassword
  } = useAuth();
  const navigate = useNavigate();
  const {
    items,
    clearCart
  } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderSuccess = searchParams.get('order') === 'success';
  const [showSuccess, setShowSuccess] = useState(orderSuccess);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [showEditInfo, setShowEditInfo] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const rawTab = searchParams.get('tab') as 'profile' | 'orders' | 'digital' | 'addresses' | 'cart' | null;
  // Addresses now live inside the Profile tab, so map old links onto Profile.
  const initialTab: 'profile' | 'orders' | 'digital' | 'cart' | null = rawTab === 'addresses' ? 'profile' : rawTab;
  const [activeTab, setActiveTab] = useState<'profile' | 'orders' | 'digital' | 'cart'>(initialTab || 'orders');
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const handleResendVerification = async () => {
    setResendState('sending');
    setResendError(null);
    try {
      await resendVerification();
      setResendState('sent');
      setTimeout(() => setResendState('idle'), 6000);
    } catch (err: any) {
      setResendState('error');
      setResendError(err?.message ?? 'Could not send verification email. Try again.');
    }
  };
  useEffect(() => {
    if (orderSuccess) {
      // Tally the just-purchased supplements so best-seller ranking reflects
      // real demand. (In production the Stripe webhook is the authoritative
      // writer; this is the client-side bridge so the system also accumulates
      // sales immediately after checkout.) Capture items BEFORE clearing.
      const supplementSales = items.filter(i => i.type === 'supplement').map(i => ({
        productId: i.productId,
        quantity: i.quantity
      }));
      if (supplementSales.length > 0) {
        recordProductSales(supplementSales).catch(err => console.error('[Sales] Failed to record product sales:', err));
      }
      clearCart();
      const next = new URLSearchParams(searchParams);
      next.delete('order');
      setSearchParams(next, {
        replace: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSuccess]);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate]);
  // Subscribe to orders
  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const unsub = subscribeToOrders(user.uid, next => {
      setOrders(next);
      setOrdersLoading(false);
    });
    return unsub;
  }, [user]);
  const handleSignOut = async () => {
    await signOut();
    navigate('/', {
      replace: true
    });
  };
  if (authLoading || !user) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center pt-32 pb-20" data-id="element-1635">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1636" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'there';
  const firstName = displayName.split(' ')[0];
  const displayOrders = orders.map(o => ({
    ...o,
    lineItems: o.lineItems.filter(i => activeTab === 'digital' ? i.type === 'book' : i.type !== 'book')
  })).filter(o => o.lineItems.length > 0);
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1637">
      <AnimatePresence data-id="element-1638">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl" data-id="element-1639">
            <div className="bg-tealslate text-white shadow-2xl rounded-xl p-5 flex items-start gap-4" data-id="element-1640">
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" data-id="element-1641" />
              <div className="flex-1" data-id="element-1642">
                <p className="font-bold text-lg mb-1" data-id="element-1643">Order received.</p>
                <p className="text-sm text-white/90 leading-relaxed" data-id="element-1644">
                  Thanks for your purchase — your order will appear below in a
                  moment. A receipt has been emailed to you.
                </p>
              </div>
              <button onClick={() => setShowSuccess(false)} className="text-white/70 hover:text-white transition-colors" aria-label="Dismiss" data-id="element-1645">
                <X className="w-4 h-4" data-id="element-1646" />
              </button>
            </div>
          </motion.div>}

        {showEditInfo && <EditInfoModal currentName={displayName} currentEmail={user.email ?? ''} onClose={() => setShowEditInfo(false)} onSaveName={updateDisplayName} onSaveEmail={updateEmail} data-id="element-1647" />}

        {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} onChangePassword={changePassword} data-id="element-1648" />}
      </AnimatePresence>

      {/* HEADER */}
      <section className="pt-32 pb-8 px-6" data-id="element-1649">
        <div className="max-w-5xl mx-auto" data-id="element-1650">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1651">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] mb-2 tracking-tight" data-id="element-1652">
              Welcome back, Jack!
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-1653">
              Manage your profile and orders
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-24 px-6" data-id="element-1654">
        <div className="max-w-5xl mx-auto" data-id="element-1655">
          {/* MAIN CONTENT */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-1656">
            <div className="flex items-center gap-4 border-b border-[#E5E5E5] mb-8 overflow-x-auto hide-scrollbar" data-id="element-1657">
              <button onClick={() => {
              setActiveTab('profile');
              setSearchParams({
                tab: 'profile'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'profile' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1658">
                Profile
              </button>
              <button onClick={() => {
              setActiveTab('orders');
              setSearchParams({});
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'orders' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1659">
                Orders
              </button>
              <button onClick={() => {
              setActiveTab('digital');
              setSearchParams({
                tab: 'digital'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'digital' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1660">
                Digital Orders
              </button>
              <button onClick={() => {
              setActiveTab('cart');
              setSearchParams({
                tab: 'cart'
              });
            }} className={`pb-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'cart' ? 'border-tealslate text-[#111111]' : 'border-transparent text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1661">
                Cart
              </button>
            </div>

            {activeTab === 'profile' ? <div className="space-y-8" data-id="element-1662">
                <div className="max-w-2xl space-y-4" data-id="element-1663">
                  <div className="flex items-center justify-between gap-4" data-id="element-1664">
                    <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1665">
                      Account Information
                    </h2>
                    <button onClick={() => setShowEditInfo(true)} className="shrink-0 text-xs font-bold py-2 px-3 bg-white border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate transition-colors flex items-center gap-2 shadow-sm" aria-label="Edit account information" data-id="element-1666">
                      <Edit2 className="w-3.5 h-3.5" data-id="element-1667" />
                      Edit
                    </button>
                  </div>

                  <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1668">
                    <dl className="divide-y divide-[#E5E5E5]" data-id="element-1669">
                      <div className="flex items-center justify-between gap-4 py-3 first:pt-0" data-id="element-1670">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1671">
                          Name
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right" data-id="element-1672">
                          {displayName}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3" data-id="element-1673">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1674">
                          Email
                        </dt>
                        <dd className="text-sm font-medium text-[#111111] text-right break-all flex items-center gap-2" data-id="element-1675">
                          <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-1676" />
                          {user.email}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 last:pb-0" data-id="element-1677">
                        <dt className="text-xs font-bold uppercase tracking-wider text-[#9A9A90]" data-id="element-1678">
                          Password
                        </dt>
                        <dd className="flex items-center gap-3" data-id="element-1679">
                          <span className="text-sm font-medium text-[#111111] tracking-widest" data-id="element-1680">
                            ••••••••
                          </span>
                          <button onClick={() => setShowChangePassword(true)} className="text-xs font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1681">
                            Change
                          </button>
                        </dd>
                      </div>
                    </dl>

                    {!user.emailVerified && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-1682">
                        <div className="flex items-start gap-2 mb-4" data-id="element-1683">
                          <AlertCircle className="w-4 h-4 text-burgundy shrink-0 mt-0.5" data-id="element-1684" />
                          <p className="text-xs text-[#5A5A5A] leading-relaxed" data-id="element-1685">
                            Email not verified. Check your inbox for a
                            verification link.
                          </p>
                        </div>
                        <button onClick={handleResendVerification} disabled={resendState === 'sending' || resendState === 'sent'} className="w-full text-xs font-bold py-3 px-4 border border-[#E5E5E5] rounded-md text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1686">
                          {resendState === 'sending' && <>
                              <span className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" data-id="element-1687" />
                              Sending…
                            </>}
                          {resendState === 'sent' && <>
                              <CheckCircle2 className="w-4 h-4" data-id="element-1688" />
                              Verification email sent
                            </>}
                          {(resendState === 'idle' || resendState === 'error') && <>
                              <Mail className="w-4 h-4" data-id="element-1689" />
                              Resend verification email
                            </>}
                        </button>
                        {resendState === 'error' && resendError && <p className="text-xs text-burgundy mt-3 leading-relaxed" data-id="element-1690">
                            {resendError}
                          </p>}
                        {resendState === 'sent' && <p className="text-xs text-[#9A9A90] mt-3 leading-relaxed" data-id="element-1691">
                            Check your inbox (and spam folder). The link expires
                            in 24 hours.
                          </p>}
                      </div>}
                  </div>
                </div>

                <AddressBookTab data-id="element-1692" />

                <button onClick={handleSignOut} className="w-full max-w-2xl bg-white border border-[#E5E5E5] shadow-sm rounded-xl p-5 hover:border-tealslate transition-all group flex items-center justify-between" data-id="element-1693">
                  <span className="text-sm font-bold text-[#111111] group-hover:text-tealslate transition-colors" data-id="element-1694">
                    Sign Out
                  </span>
                  <LogOut className="w-4 h-4 text-[#9A9A90] group-hover:text-tealslate transition-colors" data-id="element-1695" />
                </button>
              </div> : activeTab === 'orders' || activeTab === 'digital' ? <>
                {ordersLoading ? <div className="bg-white border border-[#E5E5E5] p-12 flex items-center justify-center" data-id="element-1696">
                    <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1697" />
                  </div> : ordersError ? <div className="bg-white border border-[#E5E5E5] p-6 flex gap-3 items-start" data-id="element-1698">
                    <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1699" />
                    <p className="text-sm text-burgundy" data-id="element-1700">{ordersError}</p>
                  </div> : displayOrders.length === 0 ? <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1701">
                    <Package className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1702" />
                    <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1703">
                      {activeTab === 'digital' ? 'No digital orders yet.' : 'No physical orders yet.'}
                    </h3>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1704">
                      When you make a purchase, your orders will appear here.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1705">
                      <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1706">
                        Browse Books <ArrowRight className="w-4 h-4" data-id="element-1707" />
                      </Link>
                      <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1708">
                        Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1709" />
                      </Link>
                    </div>
                  </div> : <ul className="space-y-6" data-id="element-1710">
                    {displayOrders.map(order => <li key={order.id} className="bg-white border border-[#E5E5E5] overflow-hidden" data-id="element-1711">
                        {/* Header Strip */}
                        <div className="bg-[#F9F9F9] border-b border-[#E5E5E5] px-6 py-4 flex flex-wrap items-start justify-between gap-6" data-id="element-1712">
                          <div className="flex flex-wrap items-start gap-8 md:gap-12" data-id="element-1713">
                            <div data-id="element-1714">
                              <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1715">
                                Order Placed
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1716">
                                {formatDate(order.createdAt)}
                              </p>
                            </div>
                            <div data-id="element-1717">
                              <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1718">
                                Total
                              </p>
                              <p className="text-sm text-[#111111]" data-id="element-1719">
                                {formatPrice(order.total)}
                              </p>
                            </div>
                            {order.shippingAddress && <div data-id="element-1720">
                                <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1721">
                                  Ship To
                                </p>
                                <p className="text-sm text-[#111111]" data-id="element-1722">
                                  {order.shippingAddress.line1}
                                </p>
                              </div>}
                          </div>
                          <div className="text-right" data-id="element-1723">
                            <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-wider mb-1" data-id="element-1724">
                              Order # {order.id.slice(0, 8).toUpperCase()}
                            </p>
                            <button className="text-sm text-tealslate hover:text-navyslate font-medium transition-colors" data-id="element-1725">
                              Order details
                            </button>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6" data-id="element-1726">
                          <div className="mb-6" data-id="element-1727">
                            <h3 className="text-lg font-bold text-tealslate mb-1" data-id="element-1728">
                              {order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered' ? 'Delivered' : order.fulfillmentStatus === 'shipped' ? 'Shipped' : 'Processing'}
                            </h3>
                            {order.trackingNumber && <p className="text-sm text-[#5A5A5A] flex items-center gap-1.5" data-id="element-1729">
                                <Truck className="w-4 h-4" data-id="element-1730" />
                                {order.trackingCarrier ?? 'Tracking'} ·{' '}
                                {order.trackingNumber}
                              </p>}
                          </div>

                          <ul className="space-y-8" data-id="element-1731">
                            {order.lineItems.map((item, idx) => {
                      const image = getItemImage(item);
                      return <li key={idx} className="flex flex-col sm:flex-row items-start justify-between gap-6" data-id="element-1732">
                                  {/* Left side: details and actions */}
                                  <div className="flex-1" data-id="element-1733">
                                    <p className="font-bold text-[#111111] text-base mb-1" data-id="element-1734">
                                      {item.name}
                                    </p>
                                    <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-1735">
                                      Qty {item.quantity} ·{' '}
                                      {item.type === 'book' ? 'Digital' : 'Physical'}{' '}
                                      ·{' '}
                                      {formatPrice(item.unitPrice * item.quantity)}
                                    </p>

                                    <div className="flex flex-wrap gap-3" data-id="element-1736">
                                      {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-1737" />}
                                      <Link to={item.type === 'book' ? '/books' : '/shop'} className="px-5 py-3 bg-[#F9F9F9] border border-[#E5E5E5] text-[#111111] text-xs font-bold uppercase tracking-wider hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center rounded-md" data-id="element-1738">
                                        Buy it again
                                      </Link>
                                    </div>
                                  </div>

                                  {/* Right side: image */}
                                  {image && <div className="w-60 h-60 sm:w-80 sm:h-80 shrink-0 overflow-hidden flex items-center justify-center p-2" data-id="element-1739">
                                      <img src={image} alt={item.name} className="w-full h-full object-contain" data-id="element-1740" />
                                    </div>}
                                </li>;
                    })}
                          </ul>
                        </div>
                      </li>)}
                  </ul>}
              </> : <CartTab data-id="element-1741" />}
          </motion.div>
        </div>
      </section>
    </div>;
}
function AddressBookTab() {
  const {
    profile,
    updateProfile
  } = useAuth();
  const addresses = profile?.addresses ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const handleSetDefault = async (id: string) => {
    const updated = addresses.map(a => ({
      ...a,
      isDefault: a.id === id
    }));
    await updateProfile({
      addresses: updated
    });
  };
  const handleDelete = async (id: string) => {
    const updated = addresses.filter(a => a.id !== id);
    // If we deleted the default, make the first remaining one default
    if (addresses.find(a => a.id === id)?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    await updateProfile({
      addresses: updated
    });
  };
  if (isAdding || editingId) {
    const addressToEdit = editingId ? addresses.find(a => a.id === editingId) : null;
    return <AddressForm initialData={addressToEdit} onCancel={() => {
      setIsAdding(false);
      setEditingId(null);
    }} onSave={async addr => {
      let updated: Address[];
      if (editingId) {
        updated = addresses.map(a => a.id === editingId ? {
          ...addr,
          id: editingId,
          isDefault: a.isDefault
        } : a);
      } else {
        const isFirst = addresses.length === 0;
        updated = [...addresses, {
          ...addr,
          id: Math.random().toString(36).substring(2, 9),
          isDefault: isFirst
        }];
      }
      await updateProfile({
        addresses: updated
      });
      setIsAdding(false);
      setEditingId(null);
    }} data-id="element-1742" />;
  }
  return <div className="space-y-6" data-id="element-1743">
      <div className="flex items-center justify-between" data-id="element-1744">
        <h2 className="text-2xl font-bold text-[#111111] tracking-tight" data-id="element-1745">
          Saved Addresses
        </h2>
        <button onClick={() => setIsAdding(true)} className="px-4 py-2 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate transition-colors rounded-md shadow-sm flex items-center gap-2" data-id="element-1746">
          <Plus className="w-4 h-4" data-id="element-1747" />
          Add New
        </button>
      </div>

      {addresses.length === 0 ? <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1748">
          <MapPin className="w-12 h-12 text-[#9A9A90] mx-auto mb-4" data-id="element-1749" />
          <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1750">
            No saved addresses.
          </h3>
          <p className="text-sm text-[#5A5A5A] leading-relaxed mb-8 max-w-sm mx-auto" data-id="element-1751">
            Add a shipping address to speed up your checkout process.
          </p>
          <button onClick={() => setIsAdding(true)} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1752">
            Add Address
          </button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1753">
          {addresses.map(addr => <div key={addr.id} className="bg-white border border-[#E5E5E5] p-6 flex flex-col" data-id="element-1754">
              <div className="flex-1" data-id="element-1755">
                <div className="flex items-start justify-between mb-2" data-id="element-1756">
                  <p className="font-bold text-[#111111]" data-id="element-1757">
                    {addr.recipientName}
                  </p>
                  {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-1758">
                      Default
                    </span>}
                </div>
                <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6" data-id="element-1759">
                  {addr.line1}
                  {addr.line2 && <br data-id="element-1760" />}
                  {addr.line2}
                  <br data-id="element-1761" />
                  {addr.city}, {addr.state} {addr.postalCode}
                  <br data-id="element-1762" />
                  {addr.country}
                </p>
              </div>
              <div className="flex items-center gap-4 pt-4 border-t border-[#E5E5E5]" data-id="element-1763">
                <button onClick={() => setEditingId(addr.id)} className="text-sm font-bold text-[#5A5A5A] hover:text-tealslate transition-colors flex items-center gap-1.5" data-id="element-1764">
                  <Edit2 className="w-3.5 h-3.5" data-id="element-1765" /> Edit
                </button>
                <button onClick={() => handleDelete(addr.id)} className="text-sm font-bold text-[#5A5A5A] hover:text-burgundy transition-colors flex items-center gap-1.5" data-id="element-1766">
                  <Trash2 className="w-3.5 h-3.5" data-id="element-1767" /> Delete
                </button>
                {!addr.isDefault && <button onClick={() => handleSetDefault(addr.id)} className="ml-auto text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1768">
                    Set as Default
                  </button>}
              </div>
            </div>)}
        </div>}
    </div>;
}
function AddressForm({
  initialData,
  onCancel,
  onSave
}: {
  initialData: Address | null;
  onCancel: () => void;
  onSave: (addr: Omit<Address, 'id' | 'isDefault'>) => Promise<void>;
}) {
  const [recipientName, setRecipientName] = useState(initialData?.recipientName ?? '');
  const [line1, setLine1] = useState(initialData?.line1 ?? '');
  const [line2, setLine2] = useState(initialData?.line2 ?? '');
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');
  const [postalCode, setPostalCode] = useState(initialData?.postalCode ?? '');
  const [country, setCountry] = useState(initialData?.country ?? 'United States');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await onSave({
      recipientName: recipientName.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      country: country.trim()
    });
    setSubmitting(false);
  };
  return <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1769">
      <h2 className="text-xl font-bold text-[#111111] mb-6 tracking-tight" data-id="element-1770">
        {initialData ? 'Edit Address' : 'Add New Address'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1771">
        <div data-id="element-1772">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1773">
            Recipient Full Name *
          </label>
          <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1774" />
        </div>
        <div data-id="element-1775">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1776">
            Address Line 1 *
          </label>
          <input type="text" required value={line1} onChange={e => setLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1777" />
        </div>
        <div data-id="element-1778">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1779">
            Address Line 2 (Optional)
          </label>
          <input type="text" value={line2} onChange={e => setLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1780" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1781">
          <div data-id="element-1782">
            <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1783">
              City *
            </label>
            <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1784" />
          </div>
          <div className="grid grid-cols-2 gap-5" data-id="element-1785">
            <div data-id="element-1786">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1787">
                State / Province *
              </label>
              <input type="text" required value={state} onChange={e => setState(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1788" />
            </div>
            <div data-id="element-1789">
              <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1790">
                ZIP / Postal *
              </label>
              <input type="text" required value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1791" />
            </div>
          </div>
        </div>
        <div data-id="element-1792">
          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1793">
            Country *
          </label>
          <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1794" />
        </div>

        <div className="flex items-center gap-3 pt-4" data-id="element-1795">
          <button type="submit" disabled={submitting} className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1796">
            {submitting ? 'Saving...' : 'Save Address'}
          </button>
          <button type="button" onClick={onCancel} disabled={submitting} className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm disabled:opacity-60" data-id="element-1797">
            Cancel
          </button>
        </div>
      </form>
    </div>;
}
function CartTab() {
  const {
    items,
    subtotal,
    itemCount,
    removeItem,
    updateQuantity
  } = useCart();
  const navigate = useNavigate();
  if (items.length === 0) {
    return <div className="bg-white border border-[#E5E5E5] p-12 text-center" data-id="element-1798">
        <div className="w-16 h-16 bg-[#F9F9F9] border border-[#E5E5E5] rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-1799">
          <Package className="w-7 h-7 text-[#9A9A90]" data-id="element-1800" />
        </div>
        <h3 className="text-xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-1801">
          Your cart is empty.
        </h3>
        <p className="text-sm font-medium text-[#5A5A5A] mb-8 max-w-sm mx-auto leading-relaxed" data-id="element-1802">
          Browse the bookshop or shop to add items.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1803">
          <Link to="/books" className="px-6 py-3.5 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-1804">
            Browse Books <ArrowRight className="w-4 h-4" data-id="element-1805" />
          </Link>
          <Link to="/shop" className="px-6 py-3.5 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-1806">
            Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1807" />
          </Link>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1808">
      <div data-id="element-1809">
        <h2 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-1810">
          Shopping Cart
        </h2>
      </div>

      <div className="bg-white border border-[#E5E5E5]" data-id="element-1811">
        {/* Column header */}
        <div className="px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-end" data-id="element-1812">
          <span className="text-sm font-medium text-[#9A9A90]" data-id="element-1813">Price</span>
        </div>

        <ul data-id="element-1814">
          {items.map(item => {
          const image = getItemImage(item as unknown as OrderLineItem);
          return <li key={item.productId} className="px-6 py-6 border-b border-[#E5E5E5] last:border-b-0 flex gap-5" data-id="element-1815">
                {/* Image */}
                {image && <div className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-1816">
                    <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-1817" />
                  </div>}

                {/* Details */}
                <div className="flex-1 min-w-0" data-id="element-1818">
                  <p className="text-base sm:text-lg font-bold text-[#111111] leading-snug" data-id="element-1819">
                    {item.name}
                  </p>
                  <p className="text-sm text-tealslate font-medium mt-1" data-id="element-1820">
                    In Stock
                  </p>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-1821">
                    {item.type === 'book' ? 'Digital Book' : 'Supplement'}
                  </p>

                  {/* Controls row */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mt-4" data-id="element-1822">
                    <div className="flex items-center border border-[#E5E5E5] rounded-md overflow-hidden" data-id="element-1823">
                      <button onClick={() => updateQuantity(item.productId, item.quantity - 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Decrease quantity" data-id="element-1824">
                        <Minus className="w-4 h-4" data-id="element-1825" />
                      </button>
                      <span className="px-4 text-sm font-bold text-[#111111] min-w-[2rem] text-center" data-id="element-1826">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.productId, item.quantity + 1)} className="px-3 py-1.5 text-[#5A5A5A] hover:bg-[#F9F9F9] transition-colors" aria-label="Increase quantity" data-id="element-1827">
                        <Plus className="w-4 h-4" data-id="element-1828" />
                      </button>
                    </div>

                    <span className="w-px h-5 bg-[#E5E5E5] hidden sm:block" data-id="element-1829" />

                    <button onClick={() => removeItem(item.productId)} className="text-sm font-medium text-[#5A5A5A] hover:text-burgundy transition-colors inline-flex items-center gap-1.5" data-id="element-1830">
                      <Trash2 className="w-4 h-4" data-id="element-1831" />
                      Delete
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="shrink-0 text-right" data-id="element-1832">
                  <p className="text-base sm:text-lg font-bold text-[#111111]" data-id="element-1833">
                    {formatPrice(item.unitPrice * item.quantity)}
                  </p>
                </div>
              </li>;
        })}
        </ul>

        {/* Subtotal line */}
        <div className="px-6 py-5 border-t border-[#E5E5E5] text-right" data-id="element-1834">
          <p className="text-lg text-[#111111]" data-id="element-1835">
            Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
            <span className="font-bold" data-id="element-1836">{formatPrice(subtotal)}</span>
          </p>
        </div>
      </div>

      {/* Checkout block */}
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8" data-id="element-1837">
        <p className="text-sm text-[#5A5A5A] mb-6" data-id="element-1838">
          Shipping and taxes calculated at checkout.
        </p>
        <button onClick={() => navigate('/checkout')} className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-3" data-id="element-1839">
          Pre-Order <ArrowRight className="w-4 h-4" data-id="element-1840" />
        </button>
      </div>
    </div>;
}